// i18n - Multi-language support with browser detection

export type Language = 'fr' | 'en';
export type Currency = 'EUR' | 'USD';

interface TranslationStrings {
  // Header
  demo: string;
  login: string;
  signup: string;
  
  // Dashboard Premium - Trust Score
  trustScoreTitle: string;
  trustScoreOptimized: string;
  trustScoreAvatar: string;
  trustScoreGoogle: string;
  trustScoreFirstReview: string;
  trustScorePlatforms: string;
  
  // Dashboard Premium - KPIs
  kpiGlobalRating: string;
  kpiReviewsVolume: string;
  kpiTimeSaved: string;
  kpiTimeSavedUnit: string;
  kpiThisMonth: string;
  kpiWithAI: string;
  
  // Dashboard Premium - Sentiment Weather
  sentimentWeatherTitle: string;
  sentimentStrengths: string;
  sentimentWeaknesses: string;
  sentimentEmpty: string;
  sentimentEmptyDesc: string;
  sentimentUpdatedAt: string;
  sentimentAnalyzeHistory: string;
  sentimentAnalyzing: string;
  sentimentAnalyzed: string;
  
  // Dashboard Premium - Trust Score Checklist
  trustScoreCompleteProfile: string;
  
  // Dashboard Premium - Actions Required
  actionsRequiredTitle: string;
  actionsRequiredEmpty: string;
  actionsRequiredEmptyDesc: string;
  actionsQuickReply: string;
  actionsViewAll: string;
  actionsPending: string;
  actionsAgo: string;
  
  // Unsubscribe page
  unsubProcessing: string;
  unsubWait: string;
  unsubSuccess: string;
  unsubSuccessDesc: string;
  unsubResubscribe: string;
  unsubManageSettings: string;
  unsubResubscribed: string;
  unsubResubscribedDesc: string;
  unsubGoToDashboard: string;
  unsubError: string;
  unsubErrorInvalidLink: string;
  unsubErrorGeneric: string;
  unsubErrorResubscribe: string;
  unsubManageManually: string;
  unsubReturnHome: string;
  unsubFooter: string;
  unsubDashboardSettings: string;
  
  // Pagination
  paginationPrevious: string;
  paginationNext: string;
  paginationGoToPrevious: string;
  paginationGoToNext: string;
  paginationMorePages: string;
  
  // Mobile menu
  openMenu: string;
  closeMenu: string;
  
  // Form placeholders
  placeholderFirstName: string;
  placeholderLastName: string;
  placeholderEmail: string;
  placeholderPassword: string;
  placeholderJobTitle: string;
  placeholderBusinessName: string;
  placeholderAddress: string;
  placeholderCity: string;
  placeholderGoogleLink: string;
  
  // Form labels
  firstName: string;
  lastName: string;
  jobTitle: string;
  optional: string;
  jobTitlePlaceholder: string;
  myAccount: string;
  
  // Hero
  heroBadge: string;
  heroTitle1: string;
  heroTitle2: string;
  heroSubtitle: string;
  ctaFree: string;
  ctaDemo: string;
  
  // Features
  feature1Title: string;
  feature1Desc: string;
  feature2Title: string;
  feature2Desc: string;
  feature3Title: string;
  feature3Desc: string;
  
  // Dashboard Mockup (landing page)
  mockupTitle: string;
  mockupSubtitle: string;
  mockupRating: string;
  mockupReviews: string;
  mockupGrowth: string;
  mockupWeeklyReviews: string;
  mockupAverageRating: string;
  mockupThisMonth: string;
  mockupNewReview: string;
  mockupJustNow: string;
  mockupMon: string;
  mockupTue: string;
  mockupWed: string;
  mockupThu: string;
  mockupFri: string;
  mockupSat: string;
  mockupSun: string;
  
  // Pricing
  price: string;
  perMonth: string;
  freeTrial: string;
  mostPopular: string;
  pricingTitle: string;
  pricingSubtitle: string;
  pricingFeature1: string;
  pricingFeature2: string;
  pricingFeature3: string;
  pricingFeature4: string;
  pricingFeature5: string;
  pricingCta: string;
  pricingGuarantee: string;
  
  // FAQ
  faqTitle: string;
  faqSubtitle: string;
  faq1Question: string;
  faq1Answer: string;
  faq2Question: string;
  faq2Answer: string;
  faq3Question: string;
  faq3Answer: string;
  faq4Question: string;
  faq4Answer: string;
  faq5Question: string;
  faq5Answer: string;
  faq6Question: string;
  faq6Answer: string;
  
  // Footer
  footerCopyright: string;
  footerTagline: string;
  footerCompliance: string;
  footerApiNotice: string;
  footerEthics: string;
  footerComplianceDisclaimer: string;
  footerLegal: string;
  footerHowItWorks: string;
  footerPricing: string;
  footerAbout: string;
  privacyPolicy: string;
  termsOfService: string;
  legalMentions: string;
  contact: string;
  
  // Navigation & Blog
  blog: string;
  resources: string;
  successStories: string;
  localSeoGuide: string;
  latestArticlesTitle: string;
  latestArticlesSubtitle: string;
  viewAllArticles: string;
  
  // Partner platforms
  partnerPlatforms: string;
  platformGoogle: string;
  platformFacebook: string;
  platformYelp: string;
  platformTripAdvisor: string;
  platformTrustpilot: string;
  
  // Success page
  successTitle: string;
  successSubtitle: string;
  successMessage: string;
  goToDashboard: string;
  successFeatureTitle: string;
  successQrCode: string;
  successSmartRouting: string;
  successDashboard: string;
  
  // Before/After
  beforeAfterTitle: string;
  before: string;
  after: string;
  reviewCount: string;
  
  // Social proof
  socialProof: string[];
  
  // Auth pages
  loginTitle: string;
  loginSubtitle: string;
  signupTitle: string;
  signupSubtitle: string;
  email: string;
  password: string;
  businessName: string;
  cityLocation: string;
  createAccount: string;
  loginButton: string;
  noAccount: string;
  hasAccount: string;
  forgotPassword: string;
  fillAllFields: string;
  passwordMinLength: string;
  accountCreated: string;
  loginSuccess: string;
  loginError: string;
  signupFeatures: string;
  orContinueWith: string;
  continueWithGoogle: string;
  
  // Password reset
  forgotPasswordTitle: string;
  forgotPasswordSubtitle: string;
  sendResetLink: string;
  resetPasswordError: string;
  resetPasswordEmailSent: string;
  checkYourEmail: string;
  resetEmailSentTo: string;
  checkSpamFolder: string;
  backToLogin: string;
  updatePasswordTitle: string;
  updatePasswordSubtitle: string;
  newPassword: string;
  confirmPassword: string;
  passwordsDontMatch: string;
  updatePasswordError: string;
  passwordUpdated: string;
  updatePasswordButton: string;
  invalidResetLink: string;
  invalidResetLinkDesc: string;
  requestNewLink: string;
  
  // Setup / Onboarding
  setupTitle: string;
  setupSubtitle: string;
  setupSearchPlaceholder: string;
  viewOnGoogleMaps: string;
  confirmAndContinue: string;
  setupNote: string;
  setupComplete: string;
  setupRequired: string;
  
  // Dashboard
  dashboardTitle: string;
  dashboardSubtitle: string;
  totalScans: string;
  thisMonth: string;
  positiveReviews: string;
  ofTotal: string;
  internalComplaints: string;
  toProcess: string;
  latestFeedbacks: string;
  messages: string;
  noFeedbackYet: string;
  shareQRCode: string;
  reviewManagement: string;
  
  // Feedbacks page
  feedbacksTitle: string;
  feedbacksSubtitle: string;
  searchPlaceholder: string;
  noResults: string;
  anonymous: string;
  
  // QR Code page
  qrCodeTitle: string;
  qrCodeSubtitle: string;
  howToUseQR: string;
  printIt: string;
  printItDesc: string;
  displayIt: string;
  displayItDesc: string;
  shareIt: string;
  shareItDesc: string;
  
  // Settings page
  settingsTitle: string;
  settingsSubtitle: string;
  businessInfo: string;
  businessLogo: string;
  uploadLogo: string;
  uploading: string;
  logoSize: string;
  googleReviewLink: string;
  googleLinkDesc: string;
  googleLinkError: string;
  saveChanges: string;
  saving: string;
  changesSaved: string;
  errorLoading: string;
  errorSaving: string;
  notifications: string;
  notificationEmail: string;
  notificationEmailDesc: string;
  
  // Notification Modes
  notificationModeTitle: string;
  notificationModeEmergency: string;
  notificationModeEmergencyDesc: string;
  notificationModeEmergencyBadge: string;
  notificationModeComplete: string;
  notificationModeCompleteDesc: string;
  notificationModeSilent: string;
  notificationModeSilentDesc: string;
  
  // AI Automation
  aiAutomationTitle: string;
  autoReplyLabel: string;
  autoReplyDesc: string;
  defaultToneLabel: string;
  toneProfessional: string;
  toneFriendly: string;
  toneConcise: string;
  toneDesc: string;
  
  // Sidebar
  dashboard: string;
  feedbacks: string;
  qrCode: string;
  analytics: string;
  settings: string;
  logout: string;
  logoutSuccess: string;
  proPlan: string;
  
  // Weekly Report
  weeklyReportLabel: string;
  weeklyReportDesc: string;
  
  // Report Settings
  reportSettingsTitle: string;
  reportFrequencyTitle: string;
  reportFrequencyWeekly: string;
  reportFrequencyWeeklyDesc: string;
  reportFrequencyMonthly: string;
  reportFrequencyMonthlyDesc: string;
  reportRecipientsTitle: string;
  reportRecipientsDesc: string;
  reportRecipientsPlaceholder: string;
  reportRecipientAdd: string;
  reportRecipientRemove: string;
  reportRecipientInvalid: string;
  reportRecipientDuplicate: string;
  
  // Capture tunnel
  howWasVisit: string;
  tapToRate: string;
  weSorry: string;
  tellUsMore: string;
  yourName: string;
  yourEmail: string;
  yourPhone: string;
  describeExperience: string;
  sendMessage: string;
  leaveGoogleReview: string;
  messageSent: string;
  messageSentDesc: string;
  errorSending: string;
  
  // Demo mode
  demoMode: string;
  demoSuccessTitle: string;
  realModeExplanation: string;
  realModeBenefits: string;
  testNegativeForm: string;
  demoEstablishment: string;
  establishment: string;
  yourReview: string;
  exampleReview: string;
  googleSimulation: string;
  
  // Validation
  nameRequired: string;
  nameMaxLength: string;
  nameInvalidChars: string;
  emailInvalid: string;
  emailTooLong: string;
  phoneRequired: string;
  phoneTooLong: string;
  phoneInvalid: string;
  messageRequired: string;
  messageMaxLength: string;
  
  // Legal pages
  legalBack: string;
  legalLastUpdated: string;
  
  // Privacy Policy
  privacyTitle: string;
  privacyIntroTitle: string;
  privacyIntroText: string;
  privacyDataCollectedTitle: string;
  privacyDataCollectedIntro: string;
  privacyDataId: string;
  privacyDataBusiness: string;
  privacyDataConnection: string;
  privacyDataPayment: string;
  privacyDataReviews: string;
  privacyDataUsageTitle: string;
  privacyDataUsageIntro: string;
  privacyDataUsage1: string;
  privacyDataUsage2: string;
  privacyDataUsage3: string;
  privacyDataUsage4: string;
  privacyDataUsage5: string;
  privacyGdprTitle: string;
  privacyGdprIntro: string;
  privacyGdprAccess: string;
  privacyGdprRectify: string;
  privacyGdprErase: string;
  privacyGdprPortability: string;
  privacyGdprObject: string;
  privacyGdprRestrict: string;
  privacyCcpaTitle: string;
  privacyCcpaIntro: string;
  privacyCcpa1: string;
  privacyCcpa2: string;
  privacyCcpa3: string;
  privacyCcpa4: string;
  privacySecurityTitle: string;
  privacySecurityText: string;
  privacyRetentionTitle: string;
  privacyRetentionText: string;
  privacyContactTitle: string;
  privacyContactText: string;
  
  // Third-Party Services & APIs (consolidated section)
  privacyThirdPartyTitle: string;
  privacyThirdPartyIntro: string;
  privacyThirdPartyGoogleTitle: string;
  privacyThirdPartyGoogleText: string;
  privacyThirdPartyMetaTitle: string;
  privacyThirdPartyMetaText: string;
  privacyThirdPartyOthersTitle: string;
  privacyThirdPartyOthersText: string;
  privacyThirdPartyDataUsageTitle: string;
  privacyThirdPartyDataUsageText: string;
  privacyThirdPartyDataCollection: string;
  privacyThirdPartyDataDisplay: string;
  privacyThirdPartyDataSharing: string;
  privacyThirdPartyDataRetention: string;
  privacyThirdPartyDataRevocation: string;

  // Google API Limited Use Disclosure (mandatory for GBP API verification)
  privacyGoogleApiTitle: string;
  privacyGoogleApiIntro: string;
  privacyGoogleApiScope: string;
  privacyGoogleApiLimitedUse: string;
  privacyGoogleApiEncryption: string;
  privacyGoogleApiNoAds: string;
  privacyGoogleApiNoSale: string;
  privacyGoogleApiNoProfile: string;
  privacyGoogleApiRevocation: string;
  privacyGoogleApiCompliance: string;
  
  // Terms of Service
  termsTitle: string;
  termsPurposeTitle: string;
  termsPurposeText: string;
  termsAccountTitle: string;
  termsAccount1: string;
  termsAccount2: string;
  termsAccount3: string;
  termsAccount4: string;
  termsServicesTitle: string;
  termsServicesIntro: string;
  termsServices1: string;
  termsServices2: string;
  termsServices3: string;
  termsServices4: string;
  termsServices5: string;
  termsServicesGoogleIntegration: string;
  termsPricingTitle: string;
  termsPricing1: string;
  termsPricing2: string;
  termsPricing3: string;
  termsPricing4: string;
  termsAcceptableTitle: string;
  termsAcceptableIntro: string;
  termsAcceptable1: string;
  termsAcceptable2: string;
  termsAcceptable3: string;
  termsAcceptable4: string;
  termsAcceptable5: string;
  termsIpTitle: string;
  termsIpText: string;
  termsTerminationTitle: string;
  termsTermination1: string;
  termsTermination2: string;
  termsTermination3: string;
  termsTermination4: string;
  termsLiabilityTitle: string;
  termsLiabilityText: string;
  termsThirdPartyLiabilityTitle: string;
  termsThirdPartyLiabilityText: string;
  termsModificationsTitle: string;
  termsModificationsText: string;
  termsLawTitle: string;
  termsLawText: string;
  termsContactTitle: string;
  termsContactText: string;
  
  // Legal Mentions
  legalTitle: string;
  legalEditorTitle: string;
  legalEditorCompany: string;
  legalEditorForm: string;
  legalEditorAddress: string;
  legalEditorEntityId: string;
  legalEditorEmail: string;
  legalEditorBrand: string;
  legalDirectorTitle: string;
  legalDirectorText: string;
  legalHostingTitle: string;
  legalHostingProvider: string;
  legalHostingAddress: string;
  legalHostingWebsite: string;
  legalIpTitle: string;
  legalIpText1: string;
  legalIpText2: string;
  legalDataTitle: string;
  legalDataText1: string;
  legalDataText2: string;
  legalCookiesTitle: string;
  legalCookiesText1: string;
  legalCookiesText2: string;
  legalCookies1: string;
  legalCookies2: string;
  legalCookies3: string;
  legalLiabilityTitle: string;
  legalLiabilityText: string;
  legalLinksTitle: string;
  legalLinksText: string;
  legalLawTitle: string;
  legalLawText: string;
  legalContactTitle: string;
  legalContactText: string;
  legalFooterCopyright: string;
  dataDeletion: string;
  dataDeletionTitle: string;
  dataDeletionIntro: string;
  dataDeletionStep1: string;
  dataDeletionStep2: string;
  dataDeletionStep3: string;
  dataDeletionAlternative: string;
}

const translations: Record<Language, TranslationStrings> = {
  fr: {
    demo: "Démo Client",
    login: "Connexion",
    signup: "S'inscrire",
    
    // Dashboard Premium - Trust Score
    trustScoreTitle: "SCORE DE CONFIANCE",
    trustScoreOptimized: "Votre réputation est optimisée à",
    trustScoreAvatar: "Photo de profil",
    trustScoreGoogle: "Google connecté",
    trustScoreFirstReview: "Premier avis reçu",
    trustScorePlatforms: "Plateformes liées",
    
    // Dashboard Premium - KPIs
    kpiGlobalRating: "NOTE GLOBALE",
    kpiReviewsVolume: "TOTAL AVIS",
    kpiTimeSaved: "TEMPS ÉCONOMISÉ",
    kpiTimeSavedUnit: "min",
    kpiThisMonth: "ce mois-ci",
    kpiWithAI: "grâce à l'IA",
    
    // Dashboard Premium - Sentiment Weather
    sentimentWeatherTitle: "MÉTÉO DES AVIS",
    sentimentStrengths: "Points forts",
    sentimentWeaknesses: "Axes d'amélioration",
    sentimentEmpty: "Analyse en attente",
    sentimentEmptyDesc: "Les insights IA apparaîtront après vos premiers avis.",
    sentimentUpdatedAt: "Mis à jour",
    sentimentAnalyzeHistory: "🔄 Analyser mon historique d'avis",
    sentimentAnalyzing: "Analyse en cours...",
    sentimentAnalyzed: "Analyse terminée !",
    
    // Dashboard Premium - Trust Score Checklist
    trustScoreCompleteProfile: "Compléter mon profil",
    
    // Dashboard Premium - Actions Required
    actionsRequiredTitle: "ACTIONS REQUISES",
    actionsRequiredEmpty: "Tout est à jour !",
    actionsRequiredEmptyDesc: "Aucun avis en attente de réponse.",
    actionsQuickReply: "Réponse rapide",
    actionsViewAll: "Voir tous les avis",
    actionsPending: "en attente",
    actionsAgo: "il y a",
    
    // Unsubscribe page
    unsubProcessing: "Traitement en cours...",
    unsubWait: "Veuillez patienter pendant que nous mettons à jour vos préférences.",
    unsubSuccess: "Désabonnement réussi",
    unsubSuccessDesc: "Vous ne recevrez plus les rapports hebdomadaires d'OuraTrust.",
    unsubResubscribe: "Vous avez changé d'avis ? Se réabonner",
    unsubManageSettings: "Gérer tous les paramètres de notification",
    unsubResubscribed: "Réabonnement réussi",
    unsubResubscribedDesc: "Vous recevrez à nouveau vos rapports hebdomadaires chaque lundi à 9h.",
    unsubGoToDashboard: "Aller au tableau de bord",
    unsubError: "Une erreur s'est produite",
    unsubErrorInvalidLink: "Lien de désabonnement invalide. Paramètres manquants.",
    unsubErrorGeneric: "Impossible de traiter votre demande. Veuillez réessayer.",
    unsubErrorResubscribe: "Échec du réabonnement. Veuillez réessayer.",
    unsubManageManually: "Gérer les paramètres manuellement",
    unsubReturnHome: "Retour à l'accueil",
    unsubFooter: "Vous pouvez toujours gérer vos préférences de notification dans les",
    unsubDashboardSettings: "paramètres du tableau de bord",
    
    // Pagination
    paginationPrevious: "Précédent",
    paginationNext: "Suivant",
    paginationGoToPrevious: "Aller à la page précédente",
    paginationGoToNext: "Aller à la page suivante",
    paginationMorePages: "Plus de pages",
    
    // Mobile menu
    openMenu: "Ouvrir le menu",
    closeMenu: "Fermer le menu",
    
    // Form placeholders
    placeholderFirstName: "Jean",
    placeholderLastName: "Dupont",
    placeholderEmail: "contact@example.com",
    placeholderPassword: "••••••••",
    placeholderJobTitle: "Ex: Gérant, Responsable Marketing...",
    placeholderBusinessName: "Restaurant Le Gourmet",
    placeholderAddress: "123 rue de Paris",
    placeholderCity: "Paris",
    placeholderGoogleLink: "https://search.google.com/local/writereview?placeid=...",
    
    // Form labels
    firstName: "Prénom",
    lastName: "Nom",
    jobTitle: "Poste occupé",
    optional: "(optionnel)",
    jobTitlePlaceholder: "Ex: Gérant, Responsable Marketing...",
    myAccount: "Mon Compte",
    
    heroBadge: "Centralisation d'avis multi-plateforme",
    heroTitle1: "Centralisez vos avis clients et transformez-les",
    heroTitle2: "en moteur de croissance",
    heroSubtitle: "Récoltez plus d'avis via QR Code, automatisez vos réponses avec l'IA, et gérez tous vos retours clients depuis un seul tableau de bord. La solution tout-en-un pour les commerçants exigeants.",
    ctaFree: "Démarrer mon essai gratuit 14 jours",
    ctaDemo: "Voir la démo client",
    
    feature1Title: "Collecte Intelligente",
    feature1Desc: "Optimisez votre présence sur les plateformes majeures (Google, Facebook, TripAdvisor, Trustpilot) grâce à notre QR Code universel.",
    feature2Title: "Relation Client",
    feature2Desc: "Recueillez les retours de vos clients via QR Code et améliorez votre service grâce à un suivi centralisé.",
    feature3Title: "IA Générative",
    feature3Desc: "Répondez à 100% de vos avis de manière personnalisée sans y passer vos soirées.",
    
    // Dashboard Mockup
    mockupTitle: "Tableau de Bord OuraTrust",
    mockupSubtitle: "Analytique en temps réel",
    mockupRating: "Note",
    mockupReviews: "Avis",
    mockupGrowth: "Croissance",
    mockupWeeklyReviews: "Avis de la semaine",
    mockupAverageRating: "Note moyenne",
    mockupThisMonth: "ce mois-ci",
    mockupNewReview: "Nouvel avis 5 étoiles !",
    mockupJustNow: "À l'instant",
    mockupMon: "Lun",
    mockupTue: "Mar",
    mockupWed: "Mer",
    mockupThu: "Jeu",
    mockupFri: "Ven",
    mockupSat: "Sam",
    mockupSun: "Dim",
    
    price: "49€",
    perMonth: "/mois",
    freeTrial: "14 jours d'essai gratuit",
    mostPopular: "Le plus populaire",
    pricingTitle: "Choisissez le plan qui vous convient",
    pricingSubtitle: "Pas de frais cachés. Annulez à tout moment.",
    pricingFeature1: "Starter : Google Business centralisé (49€/mois)",
    pricingFeature2: "Pro : Multi-plateforme Google + FB + Trustpilot + TripAdvisor (99€/mois)",
    pricingFeature3: "Business : Multi-établissements + Marque Blanche (199€/mois)",
    pricingFeature4: "Assistant IA inclus dans tous les plans",
    pricingFeature5: "14 jours d'essai gratuit",
    pricingCta: "Démarrer mon essai gratuit 14 jours",
    pricingGuarantee: "Annulable à tout moment",
    
    // FAQ
    faqTitle: "Questions fréquentes",
    faqSubtitle: "Tout ce que vous devez savoir sur OuraTrust",
    faq1Question: "Est-ce légal et conforme aux règles de Google ?",
    faq1Answer: "Oui, OuraTrust est conçu pour respecter les politiques Google API (Limited Use). Nous aidons les entreprises à suivre et répondre à leurs avis sur plusieurs plateformes (Google, Facebook, TripAdvisor, Trustpilot) de manière transparente. OuraTrust ne modifie pas, ne supprime pas et ne bloque pas les avis publiés. Les réponses IA sont des brouillons soumis à validation humaine avant publication.",
    faq2Question: "Quels types d'établissements peuvent utiliser OuraTrust ?",
    faq2Answer: "OuraTrust est conçu pour tous les professionnels recevant des avis : commerces, boutiques, restaurants, cabinets médicaux, dentistes, salons de coiffure, agences immobilières, et bien plus encore.",
    faq3Question: "Puis-je annuler à tout moment ?",
    faq3Answer: "Absolument ! Aucun engagement. Vous pouvez annuler votre abonnement à tout moment depuis votre dashboard. Votre accès reste actif jusqu'à la fin de la période de facturation en cours.",
    faq4Question: "Comment fonctionne la centralisation omnicanale ?",
    faq4Answer: "OuraTrust centralise tous vos flux d'avis (Google, Facebook, TripAdvisor, Trustpilot) sur un tableau de bord unique. Vous lisez, répondez et analysez l'ensemble de vos avis depuis une seule interface pour une gestion simplifiée et conforme aux politiques des plateformes.",
    faq5Question: "Combien de temps pour voir des résultats ?",
    faq5Answer: "La plupart de nos clients voient une amélioration de leur note Google en 2 à 4 semaines. En moyenne, nos utilisateurs passent de 3.8 à 4.6 étoiles en 3 mois.",
    faq6Question: "Comment fonctionne l'assistant IA ?",
    faq6Answer: "L'IA analyse chaque avis et prépare un brouillon de réponse personnalisé. Vous gardez le contrôle total : chaque brouillon nécessite votre validation avant publication. Vous pouvez également désactiver l'IA à tout moment.",
    
    footerCopyright: "© 2026 Apex Digital Flow LLC. Tous droits réservés. OuraTrust est une marque de Apex Digital Flow LLC.",
    footerTagline: "La solution intelligente pour centraliser et gérer vos avis clients",
    footerCompliance: "Conçu pour respecter les politiques Google API (Limited Use).",
    footerApiNotice: "OuraTrust utilise les API officielles pour Google, Facebook, TripAdvisor et Trustpilot.",
    footerEthics: "OuraTrust aide les entreprises à centraliser, analyser et répondre aux avis clients, en totale conformité avec les politiques des plateformes tierces.",
    footerComplianceDisclaimer: "OuraTrust ne modifie pas, ne supprime pas et n'empêche pas la publication d'avis sur Google ou toute autre plateforme.",
    footerHowItWorks: "Comment ça marche",
    footerPricing: "Tarifs",
    footerAbout: "À propos",
    footerLegal: "Légal",
    privacyPolicy: "Politique de confidentialité",
    termsOfService: "Conditions d'utilisation",
    legalMentions: "Mentions légales",
    contact: "Contact",
    
    blog: "Blog",
    resources: "Ressources",
    successStories: "Success Stories",
    localSeoGuide: "Guide SEO Local",
    latestArticlesTitle: "Nos derniers conseils pour votre réputation",
    latestArticlesSubtitle: "Guides experts pour piloter réputation et visibilité locale",
    viewAllArticles: "Voir tous les articles",
    
    // Partner platforms
    partnerPlatforms: "Plateformes partenaires",
    platformGoogle: "Google",
    platformFacebook: "Facebook",
    platformYelp: "Yelp",
    platformTripAdvisor: "TripAdvisor",
    platformTrustpilot: "Trustpilot",
    
    successTitle: "Bienvenue chez OuraTrust !",
    successSubtitle: "Paiement réussi",
    successMessage: "Consultez votre email pour recevoir votre Kit QR Premium et accéder à votre Dashboard.",
    goToDashboard: "Accéder au dashboard",
    successFeatureTitle: "Ce qui vous attend",
    successQrCode: "QR Code personnalisé",
    successSmartRouting: "Centralisation omnicanale",
    successDashboard: "Dashboard complet",
    
    beforeAfterTitle: "Transformez votre réputation",
    before: "Avant",
    after: "Après",
    reviewCount: "avis",
    
    socialProof: [
      "Un bistro à Paris vient de rejoindre OuraTrust",
      "Une clinique dentaire à Lyon a amélioré sa note",
      "Un restaurant à Marseille a atteint 4.8 étoiles",
      "Un salon de coiffure à Bordeaux vient de s'inscrire",
      "Une boulangerie à Nice collecte ses premiers avis",
    ],
    
    // Auth pages
    loginTitle: "Connexion",
    loginSubtitle: "Accédez à votre dashboard",
    signupTitle: "Créer un compte",
    signupSubtitle: "14 jours d'essai gratuit, puis 49€/mois",
    email: "Email",
    password: "Mot de passe",
    businessName: "Nom de l'établissement",
    cityLocation: "Ville / Localisation",
    createAccount: "Créer mon compte",
    loginButton: "Se connecter",
    noAccount: "Pas encore de compte ?",
    hasAccount: "Déjà un compte ?",
    forgotPassword: "Mot de passe oublié ?",
    fillAllFields: "Veuillez remplir tous les champs",
    passwordMinLength: "Le mot de passe doit contenir au moins 6 caractères",
    accountCreated: "Compte créé ! Redirection vers le paiement...",
    loginSuccess: "Connexion réussie !",
    loginError: "Email ou mot de passe incorrect",
    signupFeatures: "QR Code personnalisé • Dashboard analytics • Gestion des avis",
    orContinueWith: "ou",
    continueWithGoogle: "Continuer avec Google",
    
    // Password reset
    forgotPasswordTitle: "Mot de passe oublié",
    forgotPasswordSubtitle: "Entrez votre email pour recevoir un lien de réinitialisation",
    sendResetLink: "Envoyer le lien",
    resetPasswordError: "Erreur lors de l'envoi. Vérifiez votre email.",
    resetPasswordEmailSent: "Email de réinitialisation envoyé !",
    checkYourEmail: "Vérifiez votre boîte mail",
    resetEmailSentTo: "Un lien de réinitialisation a été envoyé à",
    checkSpamFolder: "Pensez à vérifier vos spams si vous ne le recevez pas.",
    backToLogin: "Retour à la connexion",
    updatePasswordTitle: "Nouveau mot de passe",
    updatePasswordSubtitle: "Choisissez un nouveau mot de passe sécurisé",
    newPassword: "Nouveau mot de passe",
    confirmPassword: "Confirmer le mot de passe",
    passwordsDontMatch: "Les mots de passe ne correspondent pas",
    updatePasswordError: "Erreur lors de la mise à jour du mot de passe",
    passwordUpdated: "Mot de passe mis à jour !",
    updatePasswordButton: "Mettre à jour",
    invalidResetLink: "Lien invalide ou expiré",
    invalidResetLinkDesc: "Ce lien de réinitialisation n'est plus valide. Veuillez en demander un nouveau.",
    requestNewLink: "Demander un nouveau lien",
    
    // Setup / Onboarding
    setupTitle: "Connectez votre établissement",
    setupSubtitle: "Recherchez votre commerce sur Google pour activer la synchronisation et la centralisation de vos avis.",
    setupSearchPlaceholder: "Rechercher votre établissement sur Google...",
    viewOnGoogleMaps: "Voir sur Google Maps",
    confirmAndContinue: "Confirmer et continuer",
    setupNote: "Vous pourrez modifier ces informations plus tard dans les paramètres.",
    setupComplete: "Établissement connecté avec succès !",
    setupRequired: "Configuration requise",
    
    // Dashboard
    dashboardTitle: "Centre de Contrôle",
    dashboardSubtitle: "Vue d'ensemble de vos avis clients",
    totalScans: "Total des scans",
    thisMonth: "Ce mois-ci",
    positiveReviews: "Avis positifs",
    ofTotal: "du total",
    internalComplaints: "Réclamations internes",
    toProcess: "À répondre",
    latestFeedbacks: "Derniers feedbacks",
    messages: "messages",
    noFeedbackYet: "Aucun feedback pour le moment",
    shareQRCode: "Partagez votre QR code pour commencer à collecter des avis",
    reviewManagement: "Gestion d'avis",
    
    // Feedbacks page
    feedbacksTitle: "Feedbacks clients",
    feedbacksSubtitle: "Consultez et gérez tous les retours clients",
    searchPlaceholder: "Rechercher par nom ou email...",
    noResults: "Aucun résultat pour cette recherche",
    anonymous: "Anonyme",
    
    // QR Code page
    qrCodeTitle: "QR Code",
    qrCodeSubtitle: "Générez et partagez votre QR Code de collecte d'avis",
    howToUseQR: "Comment utiliser votre QR Code ?",
    printIt: "Imprimez-le",
    printItDesc: "Téléchargez le QR Code et imprimez-le sur vos supports : cartes de visite, affiches, flyers, etc.",
    displayIt: "Affichez-le",
    displayItDesc: "Placez le QR Code à des endroits stratégiques : comptoir, tables, sortie de l'établissement.",
    shareIt: "Partagez-le",
    shareItDesc: "Envoyez le lien par SMS ou email à vos clients après leur visite pour collecter leurs avis.",
    
    // Settings page
    settingsTitle: "Paramètres",
    settingsSubtitle: "Configurez votre établissement et vos préférences",
    businessInfo: "Informations de l'établissement",
    businessLogo: "Logo de l'établissement",
    uploadLogo: "Télécharger",
    uploading: "Téléchargement...",
    logoSize: "PNG, JPG jusqu'à 2MB",
    googleReviewLink: "Lien Google Avis",
    googleLinkDesc: "Ce lien sera utilisé pour rediriger les clients satisfaits vers votre fiche Google",
    googleLinkError: "L'URL doit être un lien Google valide (Google Maps, g.page, goo.gl/maps)",
    saveChanges: "Enregistrer les modifications",
    saving: "Enregistrement...",
    changesSaved: "Modifications enregistrées ✓",
    errorLoading: "Erreur lors du chargement du profil",
    errorSaving: "Erreur lors de l'enregistrement. Veuillez réessayer.",
    notifications: "Notifications",
    notificationEmail: "Email de notification",
    notificationEmailDesc: "Recevez une alerte à chaque nouveau feedback négatif",
    
    // Notification Modes
    notificationModeTitle: "Mode de notification",
    notificationModeEmergency: "Mode Urgence uniquement",
    notificationModeEmergencyDesc: "Email immédiat uniquement pour les avis 1-3 étoiles. Les avis positifs sont résumés dans le rapport hebdomadaire.",
    notificationModeEmergencyBadge: "Recommandé",
    notificationModeComplete: "Mode Complet",
    notificationModeCompleteDesc: "Email pour chaque nouvel avis reçu, quelle que soit la note.",
    notificationModeSilent: "Mode Silencieux",
    notificationModeSilentDesc: "Aucune notification immédiate. Tout est regroupé dans le rapport hebdomadaire du lundi.",
    
    // AI Automation
    aiAutomationTitle: "Automatisation IA",
    autoReplyLabel: "Réponses assistées par IA",
    autoReplyDesc: "L'IA génère un brouillon de réponse personnalisée. Chaque réponse nécessite une validation explicite avant publication.",
    defaultToneLabel: "Ton par défaut",
    toneProfessional: "Professionnel",
    toneFriendly: "Amical",
    toneConcise: "Concis",
    toneDesc: "Ce ton sera utilisé pour toutes les réponses générées par l'IA.",
    
    // Sidebar
    dashboard: "Centre de Contrôle",
    feedbacks: "Feedbacks",
    qrCode: "QR Code",
    analytics: "Analytique",
    settings: "Paramètres",
    logout: "Déconnexion",
    logoutSuccess: "Déconnexion réussie",
    proPlan: "Plan Pro",
    
    // Weekly Report
    weeklyReportLabel: "Recevoir mon bilan hebdomadaire",
    weeklyReportDesc: "Recevez chaque lundi un résumé de vos performances par email.",
    
    // Report Settings
    reportSettingsTitle: "Rapports et Notifications",
    reportFrequencyTitle: "Fréquence des rapports",
    reportFrequencyWeekly: "Hebdomadaire (Lundi)",
    reportFrequencyWeeklyDesc: "Recap rapide de votre semaine avec focus sur les avis non répondus.",
    reportFrequencyMonthly: "Mensuel (1er du mois)",
    reportFrequencyMonthlyDesc: "Bilan stratégique complet avec analyse de croissance.",
    reportRecipientsTitle: "Destinataires additionnels",
    reportRecipientsDesc: "Ajoutez des emails pour recevoir les rapports (équipe, associés).",
    reportRecipientsPlaceholder: "email@exemple.com",
    reportRecipientAdd: "Ajouter",
    reportRecipientRemove: "Retirer",
    reportRecipientInvalid: "Email invalide",
    reportRecipientDuplicate: "Email déjà ajouté",
    
    // Capture tunnel
    howWasVisit: "Comment s'est passée votre visite ?",
    tapToRate: "Appuyez sur une étoile pour noter",
    weSorry: "Nous sommes navrés",
    tellUsMore: "Dites-nous comment nous améliorer pour que la direction puisse vous recontacter.",
    yourName: "Votre nom",
    yourEmail: "Votre email",
    yourPhone: "Votre téléphone",
    describeExperience: "Décrivez votre expérience...",
    sendMessage: "Envoyer mon message",
    leaveGoogleReview: "Déposer un avis sur Google",
    messageSent: "Message reçu ✓",
    messageSentDesc: "Merci pour votre confiance. Votre message a été transmis directement à la direction qui vous recontactera personnellement dans les plus brefs délais.",
    errorSending: "Erreur lors de l'envoi du message. Veuillez réessayer.",
    
    // Demo mode
    demoMode: "Mode Démo",
    demoSuccessTitle: "Génial ! Vos clients peuvent choisir leur plateforme préférée.",
    realModeExplanation: "En mode réel : le client choisit sa plateforme préférée (Google, Facebook, TripAdvisor) pour laisser son avis.",
    realModeBenefits: "✓ Booste votre référencement instantanément\n✓ Augmente votre visibilité locale\n✓ Convertit les clients en ambassadeurs",
    testNegativeForm: "Tester le formulaire de feedback (1-3 ★)",
    demoEstablishment: "Établissement Demo",
    establishment: "Établissement",
    yourReview: "Votre avis",
    exampleReview: "Excellent service, je recommande !",
    googleSimulation: "Simulation Google My Business",
    
    // Validation
    nameRequired: "Le nom est requis",
    nameMaxLength: "Le nom doit faire moins de 100 caractères",
    nameInvalidChars: "Le nom contient des caractères invalides",
    emailInvalid: "Email invalide",
    emailTooLong: "Email trop long",
    phoneRequired: "Le téléphone est requis",
    phoneTooLong: "Numéro trop long",
    phoneInvalid: "Numéro de téléphone invalide",
    messageRequired: "Le message est requis",
    messageMaxLength: "Le message doit faire moins de 2000 caractères",
    
    // Legal pages
    legalBack: "Retour",
    legalLastUpdated: "Dernière mise à jour : Janvier 2026",
    
    // Privacy Policy
    privacyTitle: "Politique de Confidentialité",
    privacyIntroTitle: "1. Introduction",
    privacyIntroText: "OuraTrust est une marque détenue et exploitée par Apex Digital Flow LLC, une société enregistrée au Nouveau-Mexique, USA (\"nous\", \"notre\", \"nos\"). Nous nous engageons à protéger la vie privée des utilisateurs de notre plateforme. Cette politique de confidentialité explique comment nous collectons, utilisons, stockons et protégeons vos informations personnelles conformément au Règlement Général sur la Protection des Données (RGPD) et au California Consumer Privacy Act (CCPA).",
    privacyDataCollectedTitle: "2. Données Collectées",
    privacyDataCollectedIntro: "Nous collectons les types de données suivants :",
    privacyDataId: "Données d'identification : nom, prénom, adresse email, numéro de téléphone",
    privacyDataBusiness: "Données professionnelles : nom de l'établissement, adresse, lien Google Business",
    privacyDataConnection: "Données de connexion : adresse IP, type de navigateur, pages visitées",
    privacyDataPayment: "Données de paiement : traitées de manière sécurisée par Stripe",
    privacyDataReviews: "Avis clients : notes, commentaires, informations de contact volontaires",
    privacyDataUsageTitle: "3. Utilisation des Données",
    privacyDataUsageIntro: "Vos données sont utilisées pour :",
    privacyDataUsage1: "Fournir et améliorer nos services",
    privacyDataUsage2: "Gérer votre compte et vos abonnements",
    privacyDataUsage3: "Vous envoyer des communications relatives au service",
    privacyDataUsage4: "Assurer la sécurité de la plateforme",
    privacyDataUsage5: "Respecter nos obligations légales",
    privacyGdprTitle: "4. Vos Droits (RGPD)",
    privacyGdprIntro: "Conformément au RGPD, vous disposez des droits suivants :",
    privacyGdprAccess: "Droit d'accès : obtenir une copie de vos données personnelles",
    privacyGdprRectify: "Droit de rectification : corriger vos données inexactes",
    privacyGdprErase: "Droit à l'effacement : demander la suppression de vos données",
    privacyGdprPortability: "Droit à la portabilité : recevoir vos données dans un format structuré",
    privacyGdprObject: "Droit d'opposition : vous opposer au traitement de vos données",
    privacyGdprRestrict: "Droit de limitation : limiter le traitement de vos données",
    privacyCcpaTitle: "5. Droits CCPA (Californie)",
    privacyCcpaIntro: "Si vous résidez en Californie, vous avez le droit de :",
    privacyCcpa1: "Savoir quelles informations personnelles sont collectées",
    privacyCcpa2: "Demander la suppression de vos informations personnelles",
    privacyCcpa3: "Refuser la vente de vos informations personnelles (nous ne vendons pas vos données)",
    privacyCcpa4: "Ne pas être discriminé pour avoir exercé vos droits",
    privacySecurityTitle: "6. Sécurité des Données",
    privacySecurityText: "Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos données, notamment le chiffrement SSL/TLS, l'authentification sécurisée, et des audits de sécurité réguliers.",
    privacyRetentionTitle: "7. Conservation des Données",
    privacyRetentionText: "Nous conservons vos données pendant la durée de votre abonnement et pendant une période de 3 ans après la fin de la relation commerciale, sauf obligation légale contraire.",
    privacyContactTitle: "8. Contact",
    privacyContactText: "Pour toute question concernant cette politique ou pour exercer vos droits, contactez notre Délégué à la Protection des Données :",
    
    // Third-Party Services & APIs
    privacyThirdPartyTitle: "9. Services Tiers et API",
    privacyThirdPartyIntro: "OuraTrust permet la centralisation des avis provenant de plateformes tierces. En utilisant ces fonctionnalités, vous acceptez les conditions de service de ces plateformes :",
    privacyThirdPartyGoogleTitle: "Google :",
    privacyThirdPartyGoogleText: "Les données sont traitées selon les conditions de l'API Google Business Profile. OuraTrust utilise le protocole OAuth 2.0 pour accéder à vos avis et y répondre directement depuis notre plateforme.",
    privacyThirdPartyMetaTitle: "Facebook/Instagram :",
    privacyThirdPartyMetaText: "Nous utilisons l'API Meta pour récupérer les avis et commentaires de vos pages professionnelles.",
    privacyThirdPartyOthersTitle: "TripAdvisor & Trustpilot :",
    privacyThirdPartyOthersText: "Nous accédons aux données publiques ou via API selon les autorisations que vous nous accordez lors de la connexion de vos comptes.",
    privacyThirdPartyDataUsageTitle: "Usage des données :",
    privacyThirdPartyDataUsageText: "OuraTrust ne collecte que les données strictement nécessaires à l'affichage et à la réponse aux avis. Nous ne vendons jamais ces données. Chaque plateforme tierce dispose de sa propre politique de confidentialité que nous vous invitons à consulter.",
    privacyThirdPartyDataCollection: "Collecte : Nous accédons uniquement aux informations relatives aux avis (nom de l'auteur, commentaire, note, date) et aux informations de base de l'établissement.",
    privacyThirdPartyDataDisplay: "Affichage : Ces données sont utilisées exclusivement pour l'affichage sur votre tableau de bord et pour faciliter la gestion de votre réputation.",
    privacyThirdPartyDataSharing: "Partage : OuraTrust ne partage, ne vend et ne transfère aucune donnée obtenue via les APIs tierces à des tiers ou à des plateformes publicitaires.",
    privacyThirdPartyDataRetention: "Conservation : Les données des avis sont synchronisées en temps réel et ne sont stockées sur nos serveurs que pour assurer le bon fonctionnement du service.",
    privacyThirdPartyDataRevocation: "Révocation : L'accès aux données peut être révoqué à tout moment par l'utilisateur via les paramètres de sécurité de chaque plateforme connectée.",

    // Google API Limited Use Disclosure
    privacyGoogleApiTitle: "10. Utilisation des Données de l'API Google — Divulgation d'Utilisation Limitée",
    privacyGoogleApiIntro: "L'utilisation par OuraTrust des informations reçues des API Google est conforme à la Politique de Données Utilisateur des Services API Google (Google API Services User Data Policy), y compris les Exigences d'Utilisation Limitée (Limited Use Requirements). Consultez la politique complète : https://developers.google.com/terms/api-services-user-data-policy",
    privacyGoogleApiScope: "Portée des données : OuraTrust accède uniquement aux données de votre Google Business Profile nécessaires au fonctionnement du service : avis clients (auteur, note, commentaire, date), informations de base de l'établissement (nom, adresse, place ID), et la possibilité de publier des réponses aux avis en votre nom.",
    privacyGoogleApiLimitedUse: "Utilisation limitée : OuraTrust n'utilise les données obtenues via les API Google que pour fournir et améliorer les fonctionnalités visibles par l'utilisateur dans notre application. Nous ne transférons pas ces données à des tiers sauf si nécessaire pour fournir le service, avec votre consentement explicite, ou pour des raisons légales.",
    privacyGoogleApiEncryption: "Chiffrement : Tous les tokens d'accès et de rafraîchissement Google sont stockés avec un chiffrement AES-256 côté serveur. Les clés de chiffrement sont gérées séparément et ne sont jamais exposées côté client.",
    privacyGoogleApiNoAds: "Pas de publicité : OuraTrust n'utilise jamais les données Google pour afficher, vendre ou distribuer des publicités, y compris le reciblage, la publicité personnalisée ou la publicité contextuelle.",
    privacyGoogleApiNoSale: "Pas de vente : OuraTrust ne vend jamais les données utilisateur Google à des tiers, pour quelque raison que ce soit.",
    privacyGoogleApiNoProfile: "Pas de profilage : OuraTrust n'utilise pas les données Google pour créer des profils utilisateurs à des fins de surveillance, de solvabilité, de prêt ou d'assurance.",
    privacyGoogleApiRevocation: "Révocation : Vous pouvez révoquer l'accès d'OuraTrust à vos données Google à tout moment via la page des autorisations de votre compte Google (https://myaccount.google.com/permissions) ou en supprimant votre compte OuraTrust depuis la page /data-deletion.",
    privacyGoogleApiCompliance: "OuraTrust respecte intégralement les Exigences d'Utilisation Limitée de Google (Limited Use Requirements) telles que définies dans la Politique de Données Utilisateur des Services API Google.",
    
    // Terms of Service
    termsTitle: "Conditions Générales d'Utilisation",
    termsPurposeTitle: "1. Objet",
    termsPurposeText: "Les présentes Conditions Générales d'Utilisation (CGU) régissent l'utilisation de la plateforme OuraTrust, un service de gestion de réputation en ligne permettant aux professionnels de collecter et gérer les avis de leurs clients.",
    termsAccountTitle: "2. Inscription et Compte",
    termsAccount1: "L'inscription est réservée aux professionnels disposant d'un établissement commercial",
    termsAccount2: "Vous devez fournir des informations exactes et à jour",
    termsAccount3: "Vous êtes responsable de la confidentialité de vos identifiants",
    termsAccount4: "Un compte par établissement est autorisé",
    termsServicesTitle: "3. Services Fournis",
    termsServicesIntro: "OuraTrust propose les services suivants :",
    termsServices1: "Génération de QR codes personnalisés pour la collecte d'avis",
    termsServices2: "Suivi et gestion des avis multi-plateforme (Google, Facebook, Trustpilot, TripAdvisor)",
    termsServices3: "Tableau de bord de suivi des statistiques",
    termsServices4: "Réponses assistées par IA avec validation humaine avant publication",
    termsServices5: "Kit d'impression professionnel",
    termsServicesGoogleIntegration: "Intégration Google Business Profile : L'utilisation de la fonctionnalité de gestion des avis Google nécessite que l'utilisateur connecte son compte Google via le protocole OAuth 2.0. L'utilisateur reconnaît qu'OuraTrust agit en tant qu'interface technique. OuraTrust n'est pas responsable des suspensions de fiches Google ou des changements de politique de Google Inc.",
    termsPricingTitle: "4. Tarification",
    termsPricing1: "L'abonnement mensuel est de 49€/mois ou $49/month",
    termsPricing2: "Une période d'essai gratuite de 14 jours est offerte",
    termsPricing3: "La facturation est mensuelle et renouvelée automatiquement",
    termsPricing4: "Les paiements sont traités de manière sécurisée par Stripe",
    termsAcceptableTitle: "5. Utilisation Acceptable",
    termsAcceptableIntro: "Il est interdit de :",
    termsAcceptable1: "Publier de faux avis ou manipuler les notes",
    termsAcceptable2: "Utiliser le service à des fins illégales",
    termsAcceptable3: "Tenter de contourner les systèmes de sécurité",
    termsAcceptable4: "Revendre ou sous-licencier le service",
    termsAcceptable5: "Collecter des données personnelles sans consentement",
    termsIpTitle: "6. Propriété Intellectuelle",
    termsIpText: "Tous les éléments de la plateforme OuraTrust (logo, design, code, contenu) sont protégés par le droit de la propriété intellectuelle. Toute reproduction non autorisée est interdite.",
    termsTerminationTitle: "7. Résiliation",
    termsTermination1: "Vous pouvez résilier votre abonnement à tout moment",
    termsTermination2: "La résiliation prend effet à la fin de la période de facturation en cours",
    termsTermination3: "Nous pouvons suspendre ou résilier un compte en cas de violation des CGU",
    termsTermination4: "Vos données seront conservées pendant 30 jours après la résiliation",
    termsLiabilityTitle: "8. Limitation de Responsabilité",
    termsLiabilityText: "OuraTrust ne peut être tenu responsable des avis laissés par les clients sur les plateformes tierces (Google, Facebook, Trustpilot, TripAdvisor). OuraTrust ne modifie pas, ne supprime pas et ne bloque pas les avis publiés sur ces plateformes.",
    termsThirdPartyLiabilityTitle: "Responsabilité liée aux Plateformes Tierces :",
    termsThirdPartyLiabilityText: "OuraTrust dépend des APIs fournies par Google, Meta, TripAdvisor et Trustpilot. Si l'une de ces plateformes modifie ses conditions d'accès, restreint ses fonctionnalités ou cesse ses services API, OuraTrust ne pourra être tenu responsable de l'interruption partielle ou totale du service de centralisation lié à cette plateforme.",
    termsModificationsTitle: "9. Modifications",
    termsModificationsText: "Nous nous réservons le droit de modifier les présentes CGU. Les utilisateurs seront informés par email de tout changement significatif au moins 30 jours avant leur entrée en vigueur.",
    termsLawTitle: "10. Droit Applicable",
    termsLawText: "Les présentes CGU sont régies par le droit de l'État du Nouveau-Mexique, USA. Tout litige sera soumis aux tribunaux compétents du Nouveau-Mexique.",
    termsContactTitle: "11. Contact",
    termsContactText: "Pour toute question concernant ces conditions :",
    
    // Legal Mentions
    legalTitle: "Mentions Légales",
    legalEditorTitle: "1. Éditeur du Site",
    legalEditorCompany: "Raison sociale : APEX DIGITAL FLOW LLC",
    legalEditorForm: "Forme juridique : Limited Liability Company (LLC) enregistrée au Nouveau-Mexique, USA",
    legalEditorAddress: "Siège social : 8206 LOUISIANA BLVD NE, STE A #8286, ALBUQUERQUE, NM 87113, USA",
    legalEditorEntityId: "Numéro d'identification (Entity ID) : 0008074461",
    legalEditorEmail: "Email : support@ouratrust.com",
    legalEditorBrand: "OuraTrust est une marque détenue et exploitée par APEX DIGITAL FLOW LLC.",
    legalDirectorTitle: "2. Directeur de la Publication",
    legalDirectorText: "Le Directeur de la publication est le gérant de la société Apex Digital Flow LLC.",
    legalHostingTitle: "3. Hébergement",
    legalHostingProvider: "Hébergeur : Lovable Cloud",
    legalHostingAddress: "Adresse : San Francisco, CA, USA",
    legalHostingWebsite: "Site web : https://lovable.dev",
    legalIpTitle: "4. Propriété Intellectuelle",
    legalIpText1: "L'ensemble du contenu du site OuraTrust (textes, images, graphismes, logo, icônes, sons, logiciels, etc.) est protégé par les lois américaines et internationales relatives à la propriété intellectuelle.",
    legalIpText2: "Toute reproduction, représentation, modification, publication, adaptation de tout ou partie des éléments du site, quel que soit le moyen ou le procédé utilisé, est interdite, sauf autorisation écrite préalable de Apex Digital Flow LLC.",
    legalDataTitle: "5. Données Personnelles",
    legalDataText1: "Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez d'un droit d'accès, de rectification, de suppression et de portabilité de vos données personnelles.",
    legalDataText2: "Pour exercer ces droits ou pour toute question sur le traitement de vos données, vous pouvez nous contacter à l'adresse : support@ouratrust.com",
    legalCookiesTitle: "6. Cookies",
    legalCookiesText1: "Le site OuraTrust utilise des cookies nécessaires au bon fonctionnement du service. Ces cookies ne collectent pas de données personnelles à des fins publicitaires.",
    legalCookiesText2: "Types de cookies utilisés :",
    legalCookies1: "Cookies de session (authentification)",
    legalCookies2: "Cookies de préférences (langue, thème)",
    legalCookies3: "Cookies de sécurité",
    legalLiabilityTitle: "7. Limitation de Responsabilité",
    legalLiabilityText: "Apex Digital Flow LLC s'efforce d'assurer au mieux l'exactitude et la mise à jour des informations diffusées sur ce site. Toutefois, OuraTrust ne peut garantir l'exactitude, la précision ou l'exhaustivité des informations mises à disposition sur ce site.",
    legalLinksTitle: "8. Liens Hypertextes",
    legalLinksText: "Le site OuraTrust peut contenir des liens hypertextes vers d'autres sites. OuraTrust n'exerce aucun contrôle sur ces sites et décline toute responsabilité quant à leur contenu.",
    legalLawTitle: "9. Droit Applicable",
    legalLawText: "Les présentes mentions légales sont soumises au droit de l'État du Nouveau-Mexique, USA. En cas de litige, les tribunaux compétents du Nouveau-Mexique seront seuls compétents.",
    legalContactTitle: "10. Contact",
    legalContactText: "Pour toute question ou réclamation, vous pouvez nous contacter :",
    legalFooterCopyright: "© 2026 APEX DIGITAL FLOW LLC. Tous droits réservés. OuraTrust est une marque de APEX DIGITAL FLOW LLC.",
    dataDeletion: "Suppression des données",
    dataDeletionTitle: "Instructions de suppression des données (Facebook)",
    dataDeletionIntro: "Conformément aux règles de Meta, vous pouvez demander la suppression de vos données d'activité liées à l'application OuraTrust.",
    dataDeletionStep1: "Allez dans les Paramètres et confidentialité de votre compte Facebook.",
    dataDeletionStep2: "Cliquez sur « Applications et sites web ».",
    dataDeletionStep3: "Recherchez « OuraTrust » et cliquez sur « Supprimer ».",
    dataDeletionAlternative: "Alternativement, vous pouvez nous contacter directement à support@ouratrust.com pour demander la suppression manuelle de vos données stockées sur notre plateforme.",
  },
  en: {
    demo: "Client Demo",
    login: "Log In",
    signup: "Sign Up",
    
    // Dashboard Premium - Trust Score
    trustScoreTitle: "TRUST SCORE",
    trustScoreOptimized: "Your reputation is optimized at",
    trustScoreAvatar: "Profile photo",
    trustScoreGoogle: "Google connected",
    trustScoreFirstReview: "First review received",
    trustScorePlatforms: "Linked platforms",
    
    // Dashboard Premium - KPIs
    kpiGlobalRating: "GLOBAL RATING",
    kpiReviewsVolume: "TOTAL REVIEWS",
    kpiTimeSaved: "TIME SAVED",
    kpiTimeSavedUnit: "min",
    kpiThisMonth: "this month",
    kpiWithAI: "thanks to AI",
    
    // Dashboard Premium - Sentiment Weather
    sentimentWeatherTitle: "REVIEW WEATHER",
    sentimentStrengths: "Strengths",
    sentimentWeaknesses: "Areas for improvement",
    sentimentEmpty: "Analysis pending",
    sentimentEmptyDesc: "AI insights will appear after your first reviews.",
    sentimentUpdatedAt: "Updated",
    sentimentAnalyzeHistory: "🔄 Analyze my review history",
    sentimentAnalyzing: "Analyzing...",
    sentimentAnalyzed: "Analysis complete!",
    
    // Dashboard Premium - Trust Score Checklist
    trustScoreCompleteProfile: "Complete my profile",
    
    // Dashboard Premium - Actions Required
    actionsRequiredTitle: "ACTIONS REQUIRED",
    actionsRequiredEmpty: "All caught up!",
    actionsRequiredEmptyDesc: "No reviews awaiting response.",
    actionsQuickReply: "Quick reply",
    actionsViewAll: "View all reviews",
    actionsPending: "pending",
    actionsAgo: "ago",
    
    // Unsubscribe page
    unsubProcessing: "Processing...",
    unsubWait: "Please wait while we update your preferences.",
    unsubSuccess: "Unsubscribed Successfully",
    unsubSuccessDesc: "You will no longer receive weekly reports from OuraTrust.",
    unsubResubscribe: "Changed your mind? Resubscribe",
    unsubManageSettings: "Manage all notification settings",
    unsubResubscribed: "Resubscribed Successfully",
    unsubResubscribedDesc: "You will receive your weekly reports again every Monday at 9 AM.",
    unsubGoToDashboard: "Go to Dashboard",
    unsubError: "Something went wrong",
    unsubErrorInvalidLink: "Invalid unsubscribe link. Missing parameters.",
    unsubErrorGeneric: "Failed to process your request. Please try again.",
    unsubErrorResubscribe: "Failed to resubscribe. Please try again.",
    unsubManageManually: "Manage settings manually",
    unsubReturnHome: "Return to homepage",
    unsubFooter: "You can always manage your notification preferences in your",
    unsubDashboardSettings: "dashboard settings",
    
    // Pagination
    paginationPrevious: "Previous",
    paginationNext: "Next",
    paginationGoToPrevious: "Go to previous page",
    paginationGoToNext: "Go to next page",
    paginationMorePages: "More pages",
    
    // Mobile menu
    openMenu: "Open menu",
    closeMenu: "Close menu",
    
    // Form placeholders
    placeholderFirstName: "John",
    placeholderLastName: "Doe",
    placeholderEmail: "contact@example.com",
    placeholderPassword: "••••••••",
    placeholderJobTitle: "E.g.: Manager, Marketing Director...",
    placeholderBusinessName: "My Business Name",
    placeholderAddress: "123 Main Street",
    placeholderCity: "New York",
    placeholderGoogleLink: "https://search.google.com/local/writereview?placeid=...",
    
    // Form labels
    firstName: "First name",
    lastName: "Last name",
    jobTitle: "Job title",
    optional: "(optional)",
    jobTitlePlaceholder: "E.g.: Manager, Marketing Director...",
    myAccount: "My Account",
    
    heroBadge: "Multi-platform review centralization",
    heroTitle1: "Centralize Your Customer Reviews and Turn Them",
    heroTitle2: "Into Your Growth Engine",
    heroSubtitle: "Collect more reviews via QR Code, automate AI-powered responses, and manage all your customer feedback from one dashboard. The all-in-one solution for ambitious local businesses.",
    ctaFree: "Start my 14-day free trial",
    ctaDemo: "See the live demo",
    
    feature1Title: "Smart Collection",
    feature1Desc: "Boost your presence on all major platforms (Google, Facebook, TripAdvisor, Trustpilot) with our universal QR Code.",
    feature2Title: "Customer Relations",
    feature2Desc: "Collect customer feedback via QR Code and improve your service with centralized follow-up.",
    feature3Title: "Generative AI",
    feature3Desc: "Respond to 100% of your reviews with personalized messages without spending your evenings on it.",
    
    // Dashboard Mockup
    mockupTitle: "OuraTrust Dashboard",
    mockupSubtitle: "Real-time analytics",
    mockupRating: "Rating",
    mockupReviews: "Reviews",
    mockupGrowth: "Growth",
    mockupWeeklyReviews: "Weekly Reviews",
    mockupAverageRating: "Average Rating",
    mockupThisMonth: "this month",
    mockupNewReview: "New 5-star review!",
    mockupJustNow: "Just now",
    mockupMon: "Mon",
    mockupTue: "Tue",
    mockupWed: "Wed",
    mockupThu: "Thu",
    mockupFri: "Fri",
    mockupSat: "Sat",
    mockupSun: "Sun",
    
    price: "$49",
    perMonth: "/month",
    freeTrial: "14-day free trial",
    mostPopular: "Most Popular",
    pricingTitle: "Choose the plan that fits your business",
    pricingSubtitle: "No hidden fees. Cancel anytime.",
    pricingFeature1: "Starter: Centralized Google Business ($49/month)",
    pricingFeature2: "Pro: Multi-platform Google + FB + Trustpilot + TripAdvisor ($99/month)",
    pricingFeature3: "Business: Multi-location + White Label ($199/month)",
    pricingFeature4: "AI Assistant included in all plans",
    pricingFeature5: "14-day free trial",
    pricingCta: "Start my 14-day free trial",
    pricingGuarantee: "Cancel anytime",
    
    // FAQ
    faqTitle: "Frequently Asked Questions",
    faqSubtitle: "Everything you need to know about OuraTrust",
    faq1Question: "Is this legal and compliant with Google's terms?",
    faq1Answer: "Absolutely. OuraTrust is designed to comply with Google API policies (Limited Use). We help businesses monitor and respond to reviews across multiple platforms (Google, Facebook, TripAdvisor, Trustpilot) transparently. OuraTrust does not modify, delete or block published reviews. AI-generated replies are drafts that require human approval before publication.",
    faq2Question: "Which types of businesses can use OuraTrust?",
    faq2Answer: "Our platform is designed for any business that relies on local trust. This includes medical clinics, law firms, auto repair shops, beauty salons, and restaurants. If you have a physical location or serve a local area, OuraTrust is built for you.",
    faq3Question: "Can I cancel my subscription at any time?",
    faq3Answer: "Yes. We offer a \"no-strings-attached\" policy. You can cancel your subscription with one click from your dashboard at any time. No hidden fees, no long-term contracts.",
    faq4Question: "How does omnichannel centralization work?",
    faq4Answer: "OuraTrust centralizes all your review streams (Google, Facebook, TripAdvisor, Trustpilot) into a single dashboard. You read, respond to, and analyze all your reviews from one interface for simplified management, in compliance with platform policies.",
    faq5Question: "How soon will I see results?",
    faq5Answer: "Most of our clients see an increase in review volume within the first 48 hours. On average, businesses see a significant boost in their overall rating and Google Maps ranking within the first 30 days of active use.",
    faq6Question: "How does the AI assistant work?",
    faq6Answer: "The AI analyzes every review and prepares a personalized draft response. You stay in full control: each draft requires your approval before publication. You can also disable the AI at any time.",
    
    footerCopyright: "© 2026 Apex Digital Flow LLC. All rights reserved. OuraTrust is a trademark of Apex Digital Flow LLC.",
    footerTagline: "The smart solution for centralizing and managing customer reviews",
    footerCompliance: "Designed to comply with Google API policies (Limited Use).",
    footerApiNotice: "OuraTrust uses official APIs for Google, Facebook, TripAdvisor, and Trustpilot.",
    footerEthics: "OuraTrust helps businesses centralize, analyze, and respond to customer reviews, in full compliance with third-party platform policies.",
    footerComplianceDisclaimer: "OuraTrust does not modify, delete, or prevent the publication of reviews on Google or any other platform.",
    footerHowItWorks: "How It Works",
    footerPricing: "Pricing",
    footerAbout: "About",
    footerLegal: "Legal",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",
    legalMentions: "Legal Mentions",
    contact: "Contact",
    
    blog: "Blog",
    resources: "Resources",
    successStories: "Success Stories",
    localSeoGuide: "Local SEO Guide",
    latestArticlesTitle: "Latest tips for your reputation",
    latestArticlesSubtitle: "Discover our expert guides to boost your Google reviews",
    viewAllArticles: "View all articles",
    
    // Partner platforms
    partnerPlatforms: "Partner Platforms",
    platformGoogle: "Google",
    platformFacebook: "Facebook",
    platformYelp: "Yelp",
    platformTripAdvisor: "TripAdvisor",
    platformTrustpilot: "Trustpilot",
    
    successTitle: "Welcome to OuraTrust!",
    successSubtitle: "Payment Successful",
    successMessage: "Check your email for your Premium QR Kit and Dashboard access.",
    goToDashboard: "Go to dashboard",
    successFeatureTitle: "What's next",
    successQrCode: "Custom QR Code",
    successSmartRouting: "Omnichannel centralization",
    successDashboard: "Complete dashboard",
    
    beforeAfterTitle: "Transform your reputation",
    before: "Before",
    after: "After",
    reviewCount: "reviews",
    
    socialProof: [
      "A bistro in Paris just joined OuraTrust",
      "A dental clinic in London improved its rating",
      "A restaurant in New York reached 4.8 stars",
      "A hair salon in Chicago just signed up",
      "A bakery in Miami is collecting its first reviews",
    ],
    
    // Auth pages
    loginTitle: "Log In",
    loginSubtitle: "Access your dashboard",
    signupTitle: "Create an account",
    signupSubtitle: "14-day free trial, then $49/month",
    email: "Email",
    password: "Password",
    businessName: "Business name",
    cityLocation: "City / Location",
    createAccount: "Create my account",
    loginButton: "Log in",
    noAccount: "Don't have an account?",
    hasAccount: "Already have an account?",
    forgotPassword: "Forgot password?",
    fillAllFields: "Please fill in all fields",
    passwordMinLength: "Password must be at least 6 characters",
    accountCreated: "Account created! Redirecting to payment...",
    loginSuccess: "Login successful!",
    loginError: "Invalid email or password",
    signupFeatures: "Custom QR Code • Dashboard analytics • Review management",
    orContinueWith: "or",
    continueWithGoogle: "Continue with Google",
    
    // Password reset
    forgotPasswordTitle: "Forgot your password?",
    forgotPasswordSubtitle: "Enter your email to receive a reset link",
    sendResetLink: "Send reset link",
    resetPasswordError: "Error sending email. Please check your email address.",
    resetPasswordEmailSent: "Password reset email sent!",
    checkYourEmail: "Check your inbox",
    resetEmailSentTo: "A reset link has been sent to",
    checkSpamFolder: "Check your spam folder if you don't receive it.",
    backToLogin: "Back to login",
    updatePasswordTitle: "New password",
    updatePasswordSubtitle: "Choose a new secure password",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    passwordsDontMatch: "Passwords don't match",
    updatePasswordError: "Error updating password",
    passwordUpdated: "Password updated!",
    updatePasswordButton: "Update password",
    invalidResetLink: "Invalid or expired link",
    invalidResetLinkDesc: "This reset link is no longer valid. Please request a new one.",
    requestNewLink: "Request new link",
    
    // Setup / Onboarding
    setupTitle: "Connect your business",
    setupSubtitle: "Search for your business on Google to start managing your reviews and reputation.",
    setupSearchPlaceholder: "Search your business on Google...",
    viewOnGoogleMaps: "View on Google Maps",
    confirmAndContinue: "Confirm and continue",
    setupNote: "You can change this information later in settings.",
    setupComplete: "Business connected successfully!",
    setupRequired: "Setup required",
    
    // Dashboard
    dashboardTitle: "Reputation Manager",
    dashboardSubtitle: "Overview of your customer reviews",
    totalScans: "Total scans",
    thisMonth: "This month",
    positiveReviews: "Positive reviews",
    ofTotal: "of total",
    internalComplaints: "Internal complaints",
    toProcess: "To respond",
    latestFeedbacks: "Latest feedbacks",
    messages: "messages",
    noFeedbackYet: "No feedback yet",
    shareQRCode: "Share your QR code to start collecting reviews",
    reviewManagement: "Review management",
    
    // Feedbacks page
    feedbacksTitle: "Customer Feedbacks",
    feedbacksSubtitle: "View and manage all customer feedback",
    searchPlaceholder: "Search by name or email...",
    noResults: "No results for this search",
    anonymous: "Anonymous",
    
    // QR Code page
    qrCodeTitle: "QR Code",
    qrCodeSubtitle: "Generate and share your review collection QR Code",
    howToUseQR: "How to use your QR Code?",
    printIt: "Print it",
    printItDesc: "Download the QR Code and print it on your materials: business cards, posters, flyers, etc.",
    displayIt: "Display it",
    displayItDesc: "Place the QR Code at strategic locations: counter, tables, exit.",
    shareIt: "Share it",
    shareItDesc: "Send the link by SMS or email to your customers after their visit to collect their reviews.",
    
    // Settings page
    settingsTitle: "Settings",
    settingsSubtitle: "Configure your business and preferences",
    businessInfo: "Business information",
    businessLogo: "Business logo",
    uploadLogo: "Upload",
    uploading: "Uploading...",
    logoSize: "PNG, JPG up to 2MB",
    googleReviewLink: "Google Review Link",
    googleLinkDesc: "This link will be used to redirect satisfied customers to your Google page",
    googleLinkError: "URL must be a valid Google link (Google Maps, g.page, goo.gl/maps)",
    saveChanges: "Save changes",
    saving: "Saving...",
    changesSaved: "Changes saved ✓",
    errorLoading: "Error loading profile",
    errorSaving: "Error saving. Please try again.",
    notifications: "Notifications",
    notificationEmail: "Notification email",
    notificationEmailDesc: "Receive an alert for each new negative feedback",
    
    // Notification Modes
    notificationModeTitle: "Notification Mode",
    notificationModeEmergency: "Emergency Only Mode",
    notificationModeEmergencyDesc: "Immediate email only for 1-3 star reviews. Positive reviews are summarized in the weekly report.",
    notificationModeEmergencyBadge: "Recommended",
    notificationModeComplete: "Complete Mode",
    notificationModeCompleteDesc: "Email for every new review received, regardless of rating.",
    notificationModeSilent: "Silent Mode",
    notificationModeSilentDesc: "No immediate notifications. Everything is grouped in the Monday weekly report.",
    
    // AI Automation
    aiAutomationTitle: "AI Automation",
    autoReplyLabel: "AI-assisted replies",
    autoReplyDesc: "AI generates a personalized draft reply. Each response requires explicit validation before publication.",
    defaultToneLabel: "Default tone",
    toneProfessional: "Professional",
    toneFriendly: "Friendly",
    toneConcise: "Concise",
    toneDesc: "This tone will be used for all AI-generated replies.",
    
    // Sidebar
    dashboard: "Reputation Manager",
    feedbacks: "Feedbacks",
    qrCode: "QR Code",
    analytics: "Analytics",
    settings: "Settings",
    logout: "Log out",
    logoutSuccess: "Logged out successfully",
    proPlan: "Pro Plan",
    
    // Weekly Report
    weeklyReportLabel: "Receive my weekly report",
    weeklyReportDesc: "Get a summary of your performance every Monday by email.",
    
    // Report Settings
    reportSettingsTitle: "Reports & Notifications",
    reportFrequencyTitle: "Report frequency",
    reportFrequencyWeekly: "Weekly (Monday)",
    reportFrequencyWeeklyDesc: "Quick recap of your week with focus on unanswered reviews.",
    reportFrequencyMonthly: "Monthly (1st of month)",
    reportFrequencyMonthlyDesc: "Complete strategic report with growth analysis.",
    reportRecipientsTitle: "Additional recipients",
    reportRecipientsDesc: "Add emails to receive reports (team, partners).",
    reportRecipientsPlaceholder: "email@example.com",
    reportRecipientAdd: "Add",
    reportRecipientRemove: "Remove",
    reportRecipientInvalid: "Invalid email",
    reportRecipientDuplicate: "Email already added",
    
    // Capture tunnel
    howWasVisit: "How was your visit?",
    tapToRate: "Tap a star to rate",
    weSorry: "We're sorry",
    tellUsMore: "Tell us how we can improve so management can get back to you.",
    yourName: "Your name",
    yourEmail: "Your email",
    yourPhone: "Your phone",
    describeExperience: "Describe your experience...",
    sendMessage: "Send my message",
    leaveGoogleReview: "Leave a Google review",
    messageSent: "Message received ✓",
    messageSentDesc: "Thank you for reaching out. Your message has been sent directly to management who will contact you personally as soon as possible.",
    errorSending: "Error sending message. Please try again.",
    
    // Demo mode
    demoMode: "Demo Mode",
    demoSuccessTitle: "Great! Your customers can choose their preferred platform.",
    realModeExplanation: "In real mode: the customer chooses their preferred platform (Google, Facebook, TripAdvisor) to leave a review.",
    realModeBenefits: "✓ Boosts your SEO instantly\n✓ Increases your local visibility\n✓ Converts customers into advocates",
    testNegativeForm: "Test the feedback form (1-3 ★)",
    demoEstablishment: "Demo Establishment",
    establishment: "Establishment",
    yourReview: "Your review",
    exampleReview: "Excellent service, I recommend!",
    googleSimulation: "Google My Business Simulation",
    
    // Validation
    nameRequired: "Name is required",
    nameMaxLength: "Name must be less than 100 characters",
    nameInvalidChars: "Name contains invalid characters",
    emailInvalid: "Invalid email",
    emailTooLong: "Email too long",
    phoneRequired: "Phone is required",
    phoneTooLong: "Phone number too long",
    phoneInvalid: "Invalid phone number",
    messageRequired: "Message is required",
    messageMaxLength: "Message must be less than 2000 characters",
    
    // Legal pages
    legalBack: "Back",
    legalLastUpdated: "Last updated: January 2026",
    
    // Privacy Policy
    privacyTitle: "Privacy Policy",
    privacyIntroTitle: "1. Introduction",
    privacyIntroText: "OuraTrust is a brand owned and operated by Apex Digital Flow LLC, a company registered in New Mexico, USA (\"we\", \"our\", \"us\"). We are committed to protecting the privacy of users of our platform. This privacy policy explains how we collect, use, store, and protect your personal information in compliance with the General Data Protection Regulation (GDPR) and the California Consumer Privacy Act (CCPA).",
    privacyDataCollectedTitle: "2. Data Collected",
    privacyDataCollectedIntro: "We collect the following types of data:",
    privacyDataId: "Identification data: name, surname, email address, phone number",
    privacyDataBusiness: "Business data: business name, address, Google Business link",
    privacyDataConnection: "Connection data: IP address, browser type, pages visited",
    privacyDataPayment: "Payment data: processed securely by Stripe",
    privacyDataReviews: "Customer reviews: ratings, comments, voluntary contact information",
    privacyDataUsageTitle: "3. Data Usage",
    privacyDataUsageIntro: "Your data is used to:",
    privacyDataUsage1: "Provide and improve our services",
    privacyDataUsage2: "Manage your account and subscriptions",
    privacyDataUsage3: "Send you service-related communications",
    privacyDataUsage4: "Ensure platform security",
    privacyDataUsage5: "Comply with our legal obligations",
    privacyGdprTitle: "4. Your Rights (GDPR)",
    privacyGdprIntro: "Under the GDPR, you have the following rights:",
    privacyGdprAccess: "Right of access: obtain a copy of your personal data",
    privacyGdprRectify: "Right to rectification: correct inaccurate data",
    privacyGdprErase: "Right to erasure: request deletion of your data",
    privacyGdprPortability: "Right to data portability: receive your data in a structured format",
    privacyGdprObject: "Right to object: object to processing of your data",
    privacyGdprRestrict: "Right to restriction: limit the processing of your data",
    privacyCcpaTitle: "5. CCPA Rights (California)",
    privacyCcpaIntro: "If you reside in California, you have the right to:",
    privacyCcpa1: "Know what personal information is collected",
    privacyCcpa2: "Request deletion of your personal information",
    privacyCcpa3: "Opt-out of the sale of your personal information (we do not sell your data)",
    privacyCcpa4: "Not be discriminated against for exercising your rights",
    privacySecurityTitle: "6. Data Security",
    privacySecurityText: "We implement appropriate technical and organizational security measures to protect your data, including SSL/TLS encryption, secure authentication, and regular security audits.",
    privacyRetentionTitle: "7. Data Retention",
    privacyRetentionText: "We retain your data for the duration of your subscription and for a period of 3 years after the end of the business relationship, unless legal obligations require otherwise.",
    privacyContactTitle: "8. Contact",
    privacyContactText: "For any questions regarding this policy or to exercise your rights, contact our Data Protection Officer:",
    
    // Third-Party Services & APIs
    privacyThirdPartyTitle: "9. Third-Party Services and APIs",
    privacyThirdPartyIntro: "OuraTrust enables the centralization of reviews from third-party platforms. By using these features, you accept the terms of service of these platforms:",
    privacyThirdPartyGoogleTitle: "Google:",
    privacyThirdPartyGoogleText: "Data is processed according to the Google Business Profile API terms. OuraTrust uses the OAuth 2.0 protocol to access your reviews and respond to them directly from our platform.",
    privacyThirdPartyMetaTitle: "Facebook/Instagram:",
    privacyThirdPartyMetaText: "We use the Meta API to retrieve reviews and comments from your business pages.",
    privacyThirdPartyOthersTitle: "TripAdvisor & Trustpilot:",
    privacyThirdPartyOthersText: "We access public data or via API according to the permissions you grant us when connecting your accounts.",
    privacyThirdPartyDataUsageTitle: "Data Usage:",
    privacyThirdPartyDataUsageText: "OuraTrust only collects data strictly necessary for displaying and responding to reviews. We never sell this data. Each third-party platform has its own privacy policy that we encourage you to review.",
    privacyThirdPartyDataCollection: "Collection: We only access information related to reviews (author name, comment, rating, date) and basic establishment information.",
    privacyThirdPartyDataDisplay: "Display: This data is used exclusively for display on your dashboard and to facilitate your reputation management.",
    privacyThirdPartyDataSharing: "Sharing: OuraTrust does not share, sell, or transfer any data obtained through third-party APIs to third parties or advertising platforms.",
    privacyThirdPartyDataRetention: "Retention: Review data is synchronized in real-time and is only stored on our servers to ensure proper service operation.",
    privacyThirdPartyDataRevocation: "Revocation: Access to data can be revoked at any time by the user through the security settings of each connected platform.",

    // Google API Limited Use Disclosure
    privacyGoogleApiTitle: "10. Google API Data Usage — Limited Use Disclosure",
    privacyGoogleApiIntro: "OuraTrust's use of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use Requirements. See the full policy: https://developers.google.com/terms/api-services-user-data-policy",
    privacyGoogleApiScope: "Data Scope: OuraTrust only accesses data from your Google Business Profile necessary to operate the service: customer reviews (author, rating, comment, date), basic establishment information (name, address, place ID), and the ability to publish replies to reviews on your behalf.",
    privacyGoogleApiLimitedUse: "Limited Use: OuraTrust only uses data obtained through Google APIs to provide and improve user-facing features in our application. We do not transfer this data to third parties except as necessary to provide the service, with your explicit consent, or for legal reasons.",
    privacyGoogleApiEncryption: "Encryption: All Google access and refresh tokens are stored with AES-256 server-side encryption. Encryption keys are managed separately and are never exposed client-side.",
    privacyGoogleApiNoAds: "No Advertising: OuraTrust never uses Google data to display, sell, or distribute advertisements, including retargeting, personalized advertising, or interest-based advertising.",
    privacyGoogleApiNoSale: "No Sale: OuraTrust never sells Google user data to third parties, for any reason whatsoever.",
    privacyGoogleApiNoProfile: "No Profiling: OuraTrust does not use Google data to build user profiles for surveillance, creditworthiness, lending, or insurance purposes.",
    privacyGoogleApiRevocation: "Revocation: You can revoke OuraTrust's access to your Google data at any time through your Google account permissions page (https://myaccount.google.com/permissions) or by deleting your OuraTrust account via the /data-deletion page.",
    privacyGoogleApiCompliance: "OuraTrust fully complies with the Google Limited Use Requirements as defined in the Google API Services User Data Policy.",
    
    // Terms of Service
    termsTitle: "Terms of Service",
    termsPurposeTitle: "1. Purpose",
    termsPurposeText: "These Terms of Service (\"Terms\") govern the use of the OuraTrust platform, an online reputation management service that enables businesses to collect and manage customer reviews.",
    termsAccountTitle: "2. Registration and Account",
    termsAccount1: "Registration is reserved for professionals with a commercial establishment",
    termsAccount2: "You must provide accurate and up-to-date information",
    termsAccount3: "You are responsible for the confidentiality of your credentials",
    termsAccount4: "One account per establishment is authorized",
    termsServicesTitle: "3. Services Provided",
    termsServicesIntro: "OuraTrust provides the following services:",
    termsServices1: "Generation of custom QR codes for review collection",
    termsServices2: "Multi-platform review monitoring and management (Google, Facebook, Trustpilot, TripAdvisor)",
    termsServices3: "Statistics tracking dashboard",
    termsServices4: "AI-assisted replies with human approval before publication",
    termsServices5: "Professional print kit",
    termsServicesGoogleIntegration: "Google Business Profile Integration: Use of the Google review management feature requires the user to connect their Google account via the OAuth 2.0 protocol. The user acknowledges that OuraTrust acts as a technical interface. OuraTrust is not responsible for Google listing suspensions or policy changes by Google Inc.",
    termsPricingTitle: "4. Pricing",
    termsPricing1: "Monthly subscription is $49/month or 49€/month",
    termsPricing2: "A 14-day free trial period is offered",
    termsPricing3: "Billing is monthly and automatically renewed",
    termsPricing4: "Payments are processed securely by Stripe",
    termsAcceptableTitle: "5. Acceptable Use",
    termsAcceptableIntro: "It is prohibited to:",
    termsAcceptable1: "Post fake reviews or manipulate ratings",
    termsAcceptable2: "Use the service for illegal purposes",
    termsAcceptable3: "Attempt to circumvent security systems",
    termsAcceptable4: "Resell or sublicense the service",
    termsAcceptable5: "Collect personal data without consent",
    termsIpTitle: "6. Intellectual Property",
    termsIpText: "All elements of the OuraTrust platform (logo, design, code, content) are protected by intellectual property law. Any unauthorized reproduction is prohibited.",
    termsTerminationTitle: "7. Termination",
    termsTermination1: "You may cancel your subscription at any time",
    termsTermination2: "Termination takes effect at the end of the current billing period",
    termsTermination3: "We may suspend or terminate an account for violation of these Terms",
    termsTermination4: "Your data will be retained for 30 days after termination",
    termsLiabilityTitle: "8. Limitation of Liability",
    termsLiabilityText: "OuraTrust cannot be held responsible for reviews left by customers on third-party platforms (Google, Facebook, Trustpilot, TripAdvisor). OuraTrust does not modify, delete or block reviews published on these platforms.",
    termsThirdPartyLiabilityTitle: "Third-Party Platform Liability:",
    termsThirdPartyLiabilityText: "OuraTrust relies on APIs provided by Google, Meta, TripAdvisor, and Trustpilot. If any of these platforms modifies its access terms, restricts its features, or discontinues its API services, OuraTrust cannot be held responsible for the partial or total interruption of the centralization service related to that platform.",
    termsModificationsTitle: "9. Modifications",
    termsModificationsText: "We reserve the right to modify these Terms. Users will be notified by email of any significant changes at least 30 days before they take effect.",
    termsLawTitle: "10. Governing Law",
    termsLawText: "These Terms are governed by the laws of the State of New Mexico, USA. Any dispute shall be submitted to the competent courts of New Mexico.",
    termsContactTitle: "11. Contact",
    termsContactText: "For any questions regarding these terms:",
    
    // Legal Mentions
    legalTitle: "Legal Mentions",
    legalEditorTitle: "1. Site Publisher",
    legalEditorCompany: "Company name: APEX DIGITAL FLOW LLC",
    legalEditorForm: "Legal form: Limited Liability Company (LLC) registered in New Mexico, USA",
    legalEditorAddress: "Registered office: 8206 LOUISIANA BLVD NE, STE A #8286, ALBUQUERQUE, NM 87113, USA",
    legalEditorEntityId: "Entity ID: 0008074461",
    legalEditorEmail: "Email: support@ouratrust.com",
    legalEditorBrand: "OuraTrust is a brand owned and operated by APEX DIGITAL FLOW LLC.",
    legalDirectorTitle: "2. Publication Director",
    legalDirectorText: "The Publication Director is the manager of Apex Digital Flow LLC.",
    legalHostingTitle: "3. Hosting",
    legalHostingProvider: "Host: Lovable Cloud",
    legalHostingAddress: "Address: San Francisco, CA, USA",
    legalHostingWebsite: "Website: https://lovable.dev",
    legalIpTitle: "4. Intellectual Property",
    legalIpText1: "All content on the OuraTrust website (texts, images, graphics, logo, icons, sounds, software, etc.) is protected by US and international intellectual property laws.",
    legalIpText2: "Any reproduction, representation, modification, publication, or adaptation of all or part of the elements of the site, by any means or process, is prohibited without prior written authorization from Apex Digital Flow LLC.",
    legalDataTitle: "5. Personal Data",
    legalDataText1: "In accordance with the General Data Protection Regulation (GDPR), you have the right to access, rectify, delete, and port your personal data.",
    legalDataText2: "To exercise these rights or for any questions about the processing of your data, you can contact us at: support@ouratrust.com",
    legalCookiesTitle: "6. Cookies",
    legalCookiesText1: "The OuraTrust website uses cookies necessary for the proper functioning of the service. These cookies do not collect personal data for advertising purposes.",
    legalCookiesText2: "Types of cookies used:",
    legalCookies1: "Session cookies (authentication)",
    legalCookies2: "Preference cookies (language, theme)",
    legalCookies3: "Security cookies",
    legalLiabilityTitle: "7. Limitation of Liability",
    legalLiabilityText: "Apex Digital Flow LLC strives to ensure the accuracy and updating of information published on this site. However, OuraTrust cannot guarantee the accuracy, precision, or completeness of the information made available on this site.",
    legalLinksTitle: "8. Hyperlinks",
    legalLinksText: "The OuraTrust website may contain hyperlinks to other sites. OuraTrust has no control over these sites and disclaims any responsibility for their content.",
    legalLawTitle: "9. Governing Law",
    legalLawText: "These legal mentions are subject to the laws of the State of New Mexico, USA. In case of dispute, the competent courts of New Mexico shall have sole jurisdiction.",
    legalContactTitle: "10. Contact",
    legalContactText: "For any questions or complaints, you can contact us:",
    legalFooterCopyright: "© 2026 APEX DIGITAL FLOW LLC. All rights reserved. OuraTrust is a trademark of APEX DIGITAL FLOW LLC.",
    dataDeletion: "Data Deletion",
    dataDeletionTitle: "Data Deletion Instructions (Facebook)",
    dataDeletionIntro: "In accordance with Meta's policies, you can request the deletion of your activity data linked to the OuraTrust application.",
    dataDeletionStep1: "Go to your Facebook account's Settings & Privacy.",
    dataDeletionStep2: "Click on 'Apps and Websites'.",
    dataDeletionStep3: "Find 'OuraTrust' and click 'Remove'.",
    dataDeletionAlternative: "Alternatively, you can contact us directly at support@ouratrust.com to request manual deletion of your data stored on our platform.",
  },
};

export function detectLanguage(pathname?: string): Language {
  const routePath = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : undefined);

  if (routePath) {
    if (routePath === '/en' || routePath.startsWith('/en/')) return 'en';
    if (routePath === '/' || !routePath.startsWith('/')) {
      return getBrowserPreferredLanguage();
    }
    return 'fr';
  }

  return getBrowserPreferredLanguage();
}

export function getBrowserPreferredLanguage(): Language {
  if (typeof navigator === 'undefined') {
    return 'fr';
  }

  const browserLanguages = Array.isArray(navigator.languages) && navigator.languages.length > 0
    ? navigator.languages
    : [navigator.language];

  return browserLanguages.some((lang) => lang?.toLowerCase().startsWith('fr')) ? 'fr' : 'en';
}

export function getCurrency(lang: Language): Currency {
  return lang === 'fr' ? 'EUR' : 'USD';
}

export function getStripeCheckoutUrl(lang: Language): string {
  // Localized Stripe checkout URLs
  if (lang === 'fr') {
    return 'https://buy.stripe.com/test_4gMbJ148P0U5aj1dHkaVa01'; // French EUR pricing
  }
  return 'https://buy.stripe.com/test_6oUcN5gVBeKV3UDfPsaVa02'; // Global USD pricing
}

export function getTranslations(lang: Language): TranslationStrings {
  return translations[lang];
}

export function getPrice(lang: Language): { amount: string; period: string } {
  const t = translations[lang];
  return { amount: t.price, period: t.perMonth };
}
